import React from "react";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import "../App.css";
import "../components/layout/privacy and policy/cookie.css";
import { Helmet } from "react-helmet-async";

const pageTitle = "Cookie Policy | AIGIO";
const pageDescription = "Learn about the cookies we use and how to manage them.";

type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

type Section = {
  id: string;
  title: string;
  number?: string;
  blocks: Block[];
};

const LAST_UPDATED = "01 July 2026";

const SECTIONS: Section[] = [
  {
    id: "what-are-cookies",
    title: "What are cookies?",
    number: "1",
    blocks: [
      {
        type: "p",
        text: "Cookies are small text files placed on your device when you visit a website. They help the site function, remember your preferences, and understand how the site is used. Similar technologies include local storage and analytics identifiers.",
      },
    ],
  },
  {
    id: "types-of-cookies",
    title: "Types of cookies we use",
    number: "2",
    blocks: [
      {
        type: "p",
        text: "The following table:",
      },
      {
        type: "table",
        headers: ["Category", "Purpose", "Can you disable?"],
        rows: [
          [
            "Essential",
            "Required to run the Service keeping you logged in, securing sessions, and remembering settings such as your theme.", "No the Service will not work properly without them.",
          ],
          [
            "Analytics",
            "Help us understand how the Service is used so we can improve it. We use Google Analytics for this.", "Yes",
          ],
          [
            "Marketing",
            "Used to measure and improve our marketing and to show relevant messages about AIGIO.",
            "Yes",
          ],
        ],
      },
    ],
  },
  {
    id: "third-party-cookies",
    title: "Third-party cookies",
    number: "3",
    blocks: [
      {
        type: "p",
        text: "Some cookies are set by third parties we use, such as Google Analytics for usage analytics and our payment gateway during checkout. These providers process data under their own privacy and cookie policies.",
      },
    ],
  },
  {
    id: "managing-cookies",
    title: "Managing cookies",
    number: "4",
    blocks: [
      {
        type: "list",
        items: [
          "You can control or delete cookies through your browser settings. Most browsers let you block or remove cookies and notify you when one is set.",
          "Blocking essential cookies may break parts of the Service, such as logging in.",
          "You can opt out of Google Analytics using Google's browser add-on or your browser's privacy controls.",
        ],
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    number: "5",
    blocks: [
      {
        type: "p",
        text: 'We may update this Cookie Policy from time to time. The "Last updated" date above reflects the current version.',
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    number: "6",
    blocks: [
      {
        type: "p",
        text: "Questions about our use of cookies? Email support@aigio.ai.",
      },
    ],
  },
];

function renderBlock(block: Block, key: number) {
  switch (block.type) {
    case "list":
      return (
        <ul
          key={key}
          className="legal-list"
          style={{ listStyleType: "disc", paddingLeft: "24px", margin: "14px 0" }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: "10px", display: "list-item" }}>
              {item}
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div key={key} style={{ overflowX: "auto", margin: "18px 0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #333" }}>
                {block.headers.map((h, i) => (
                  <th key={i} style={{ padding: "10px", fontWeight: "bold" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "10px" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return (
        <p key={key} className="legal-paragraph">
          {block.text}
        </p>
      );
  }
}

const CookiePolicy: React.FC = () => {
  const [activeId, setActiveId] = React.useState(SECTIONS[0].id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 }
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="page-shell page-shell--legal">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <Header />

        <main className="cookie-page">
          <div className="legal-page__inner">
            <p className="legal-page__kicker">LEGAL DOCUMENTATION</p>
            <h1 className="legal-page__title">Cookie Policy</h1>
            <p className="legal-page__updated">
              Last updated: {LAST_UPDATED}. AIGIO is a product of Orglife Private Limited
            </p>

            <div className="legal-page__body">
              <aside className="legal-toc" aria-label="Table of contents">
                <ul className="legal-toc__list">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`legal-toc__link ${
                          activeId === s.id ? "legal-toc__link--active" : ""
                        }`}
                      >
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="legal-content">
                {/* Upper introductory part before Section 1 */}
                <div style={{ marginBottom: "32px" }}>
                  <p className="legal-paragraph">
                    This Cookie Policy explains how AIGIO, a product of Orglife Private Limited, uses cookies and similar technologies on our website and application.
                  </p>
                  <p className="legal-paragraph">
                    It should be read together with our Privacy Policy.
                  </p>
                </div>

                {/* Numbered Sections */}
                {SECTIONS.map((s) => (
                  <section key={s.id} id={s.id} className="legal-section">
                    <h2 className="legal-section__title">
                      {s.number ? `${s.number}. ` : ""}{s.title}
                    </h2>
                    {s.blocks.map((b, j) => renderBlock(b, j))}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default CookiePolicy;